import { useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { useFreemium } from '../hooks/useFreemium'
import { getZoomAPI } from '../utils/zoomAPI'

export function UserMenu() {
  const { authState, user, signInWithZoom, signInWithGoogle, signOut } = useAuth()
  const { tier, portalUrl } = useFreemium()
  const [isOpen, setIsOpen] = useState(false)
  const [showAuthOptions, setShowAuthOptions] = useState(false)
  const [isUpgradeLoading, setIsUpgradeLoading] = useState(false)

  console.log('[UserMenu] Current tier:', tier, 'Button visible:', tier !== 'pro')

  const handleUpgradeClick = async () => {
    console.log('[UserMenu] Creating LemonSqueezy checkout...')
    setIsUpgradeLoading(true)

    try {
      // Get current session token
      const { data: { session } } = await (await import('../lib/supabase')).supabase.auth.getSession()

      if (!session || !user || !user.email || !user.id) {
        console.error('[UserMenu] No active session or user')
        alert('Please sign in first')
        return
      }

      console.log('[UserMenu] Creating checkout for:', user.email)

      // Create checkout URL directly
      const { createCheckoutUrl } = await import('../lib/lemonsqueezy')
      const checkoutUrl = await createCheckoutUrl('yearly', user.email, user.id, session.access_token)

      console.log('[UserMenu] Opening LemonSqueezy checkout:', checkoutUrl)

      // Open checkout in browser
      const zoomAPI = getZoomAPI()
      await zoomAPI.openUrl(checkoutUrl)

      setIsOpen(false)
    } catch (error) {
      console.error('[UserMenu] Failed to create checkout:', error)
      alert('Failed to open checkout. Please try again.')
    } finally {
      setIsUpgradeLoading(false)
    }
  }

const manageSubscription = async () => {
    if (!portalUrl) return

    await getZoomAPI().openUrl(portalUrl)
}

  if (authState === 'loading') {
    return (
      <div style={{ padding: '8px 16px', fontSize: '14px', color: '#6B7280' }}>
        Loading...
      </div>
    )
  }

  if (authState === 'signed-out') {
    if (showAuthOptions) {
      return (
        <div style={{ position: 'relative' }}>
          <div style={{
            position: 'absolute',
            top: 0,
            right: 0,
            backgroundColor: 'white',
            border: '1px solid #D1D5DB',
            borderRadius: '6px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            padding: '12px',
            minWidth: '250px',
            zIndex: 1000
          }}>
            <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 600, fontSize: '14px' }}>Sign In</span>
              <button
                onClick={() => setShowAuthOptions(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '18px',
                  cursor: 'pointer',
                  padding: '2px'
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {/* Zoom OAuth */}
              <button
                onClick={async () => {
                  try {
                    await signInWithZoom()
                    setShowAuthOptions(false)
                  } catch (error) {
                    alert('Failed to sign in: ' + (error instanceof Error ? error.message : String(error)))
                  }
                }}
                style={{
                  padding: '10px 16px',
                  backgroundColor: '#2563EB',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span>📹</span>
                <span>Sign in with Zoom</span>
              </button>

              {/* Google OAuth */}
              <button
                onClick={async () => {
                  try {
                    await signInWithGoogle()
                    // Don't close options - user will return from external browser
                  } catch (error) {
                    alert('Failed to sign in: ' + (error instanceof Error ? error.message : String(error)))
                  }
                }}
                style={{
                  padding: '10px 16px',
                  backgroundColor: 'white',
                  color: '#374151',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span>🔐</span>
                <span>Sign in with Google</span>
              </button>
            </div>
          </div>
        </div>
      )
    }

    return (
      <button
        onClick={() => setShowAuthOptions(true)}
        className="sign-in-button"
      >
        Sign In
      </button>
    )
  }

  // Get user initials for avatar
  const getInitials = (email: string | undefined): string => {
    if (!email) return '?';
    const parts = email.split('@')[0].split(/[._-]/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return email.substring(0, 2).toUpperCase();
  };

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="user-info"
        style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0, gap: '8px' }}
      >
        <div className="user-avatar">
          {getInitials(user?.email)}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span className="user-email">
            {user?.email}
          </span>
          {tier === 'trial' && (
            <span style={{
              backgroundColor: '#FEF3C7',
              color: '#92400E',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '10px',
              fontWeight: 600,
              whiteSpace: 'nowrap'
            }}>
              Trial
            </span>
          )}
          {tier === 'pro' && (
            <span style={{
              backgroundColor: '#DBEAFE',
              color: '#1E40AF',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '10px',
              fontWeight: 600,
              whiteSpace: 'nowrap'
            }}>
              💎 Pro
            </span>
          )}
          {tier === 'free' && (
            <span style={{
              backgroundColor: '#F3F4F6',
              color: '#6B7280',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '10px',
              fontWeight: 600,
              whiteSpace: 'nowrap'
            }}>
              Free
            </span>
          )}
        </div>
      </button>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          right: 0,
          backgroundColor: 'white',
          border: '1px solid #D1D5DB',
          borderRadius: '6px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          minWidth: '200px',
          zIndex: 1000
        }}>
          {tier !== 'pro' && (
            <button
              onClick={handleUpgradeClick}
              disabled={isUpgradeLoading}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '12px 16px',
                fontSize: '14px',
                color: isUpgradeLoading ? '#9CA3AF' : '#2563EB',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: isUpgradeLoading ? 'not-allowed' : 'pointer',
                borderBottom: '1px solid #E5E7EB'
              }}
              onMouseEnter={(e) => !isUpgradeLoading && (e.currentTarget.style.backgroundColor = '#F9FAFB')}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              {isUpgradeLoading ? '⏳ Opening checkout...' : '💎 Upgrade to Pro'}
            </button>
          )}
          {tier === 'pro' && portalUrl && (
            <button
              onClick={manageSubscription}
              rel="noopener noreferrer"
              style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '12px 16px',
                  fontSize: '14px',
                  color: '#2563EB',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  borderBottom: '1px solid #E5E7EB'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              Manage Subscription
            </button>
          )}
          <button
            onClick={() => {
              signOut()
              setIsOpen(false)
            }}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              padding: '12px 16px',
              fontSize: '14px',
              color: '#DC2626',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            Sign Out
          </button>
        </div>
      )}
    </div>
  )
}